import {AuthorizeEsiaResponsePayload} from '../../../../../shared/models/api/authorizeEsiaResponsePayload';
import {storage, StorageKeys} from '../../../../../shared/storage';
import {logoutEsiaSession} from '../../../../../redux/actions/login';
import {loadCardActivationSnapshot} from '../../../../../redux/actions/cardActivation';
import {
  assertSamePerson,
  prepareCardReissueAfterEsia,
  showEsiaMismatchAlert,
} from '../../../../../redux/actions/cardReissue';
import {loginSlice} from '../../../../../redux/reducers/login';
import {AppDispatch} from '../../../../../redux';
import {isCardReissueEsiaPending, clearCardReissueExpectedSnils} from '../../../../../shared/storage/cardReissueEsia';
import {mergeEsiaWithServerRegInfo} from '../../../cardActivation';

export type AfterLoggedEsia =
  | {kind: 'activation'}
  | {kind: 'issue'; res: AuthorizeEsiaResponsePayload}
  | {kind: 'reissue'; res: AuthorizeEsiaResponsePayload}
  | {kind: 'personMismatch'; logoutLink: string | null};

const persistActivationSession = async (dispatch: AppDispatch, res: AuthorizeEsiaResponsePayload) => {
  if (res?.token) {
    await storage.setItem(StorageKeys.activationToken, res.token);
    await storage.setItem(StorageKeys.isESIA, true);
  }
  dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
};

const rejectReissuePersonMismatch = async (dispatch: AppDispatch): Promise<AfterLoggedEsia> => {
  dispatch(showEsiaMismatchAlert());
  dispatch(loginSlice.actions.setEsiaRegistrationInfo(null));
  await storage.removeItem(StorageKeys.activationToken);
  await storage.removeItem(StorageKeys.esiaRegInfo);
  await clearCardReissueExpectedSnils();
  const logoutLink = (await dispatch(logoutEsiaSession())) as string | null;
  return {kind: 'personMismatch', logoutLink};
};

export const resolveAfterLoggedEsia = async (
  dispatch: AppDispatch,
  res: AuthorizeEsiaResponsePayload | null | undefined,
  switchToActivation: (serverRegInfo?: AuthorizeEsiaResponsePayload | null) => Promise<unknown> | unknown,
  options?: {fromReissue?: boolean},
): Promise<AfterLoggedEsia> => {
  const isReissueFlow = Boolean(options?.fromReissue) || isCardReissueEsiaPending();

  if (!res) {
    if (isReissueFlow) {
      return rejectReissuePersonMismatch(dispatch);
    }
    return {kind: 'personMismatch', logoutLink: null};
  }

  if (isReissueFlow) {
    const isSamePerson = await dispatch(assertSamePerson(res));
    await dispatch(prepareCardReissueAfterEsia(isSamePerson, res));

    if (!isSamePerson) {
      return rejectReissuePersonMismatch(dispatch);
    }

    const serverRegInfo = await loadCardActivationSnapshot(res.token);
    if (serverRegInfo) {
      await persistActivationSession(dispatch, mergeEsiaWithServerRegInfo(res, serverRegInfo));
      await Promise.resolve(switchToActivation(serverRegInfo));
      return {kind: 'activation'};
    }

    dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
    await clearCardReissueExpectedSnils();
    return {kind: 'reissue', res};
  }

  const serverRegInfo = await loadCardActivationSnapshot(res.token);
  if (serverRegInfo) {
    await persistActivationSession(dispatch, mergeEsiaWithServerRegInfo(res, serverRegInfo));
    await Promise.resolve(switchToActivation(serverRegInfo));
    return {kind: 'activation'};
  }

  return {kind: 'issue', res};
};
